/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";
import { Table, Button, Select, message, Input, Modal } from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { retrieveHareKrishnaInvoiceData, CancelInvoice } from "../../api/PupilApi";
import PaymentUpdateModal from "./PaymentUpdateModal";
import { generateInvoicePDF } from "../utils/pdfGenerator";
import { getColumns } from "../utils/tableColumns";

const InvoiceDashboard = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchId, setSearchId] = useState("");
  const [searchGlobal, setSearchGlobal] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    fetchInvoices();
  }, [selectedMonth, selectedYear]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await retrieveHareKrishnaInvoiceData(selectedMonth, selectedYear);
      const invoicesList = response?.data?.data?.filter(item =>
        item?.taxInvoiceDataInfo?.status !== "Cancelled"
      ) || [];
      if (invoicesList.length === 0) message.warning("No invoices found.");
      setInvoices(invoicesList);
    } catch {
      message.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelInvoice = async (record) => {
    const invoiceNo = record?.taxInvoiceDataInfo?.invoiceNo ?? "Unknown";
    Modal.confirm({
      title: "Confirm Cancellation",
      content: `Cancel invoice ${invoiceNo}?`,
      onOk: async () => {
        try {
          const response = await CancelInvoice(record);
          response.data.success
            ? message.success(`Invoice ${invoiceNo} canceled`)
            : message.error(`Failed to cancel ${invoiceNo}`);
          fetchInvoices();
        } catch {
          message.error("Cancellation failed");
        }
      }
    });
  };

  const columns = getColumns({
    onEdit: (record) => {
      //console.log(JSON.stringify(record))
      setSelectedInvoice(record);
      setModalOpen(true);
    },
    onDownload: (record) => {
      generateInvoicePDF(record.taxInvoiceDataInfo);
    },
    onCancel: handleCancelInvoice
  });

  return (
    <div className="p-4">
      <h2 className="text-xl font-semibold mb-4">Invoice Dashboard</h2>

      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={selectedMonth} onChange={setSelectedMonth}>
          {Array.from({ length: 12 }, (_, i) => {
            const month = (i + 1).toString().padStart(2, "0");
            return <Select.Option key={month} value={month}>{month}</Select.Option>;
          })}
        </Select>

        <Select value={selectedYear} onChange={setSelectedYear}>
          {Array.from({ length: 7 }, (_, i) => 
            <Select.Option key={2020+i} value={2020+i}>{2020+i}</Select.Option>
          )}
        </Select>

        <Button icon={<FileTextOutlined />} onClick={fetchInvoices} loading={loading}>Refresh</Button>
        <Button onClick={() => navigate("/")}>Back to Home</Button>
      </div>

      <div className="flex gap-2 mb-4">
        <Input placeholder="Search Invoice No..." value={searchId} onChange={e => setSearchId(e.target.value)} allowClear />
        <Input placeholder="Global Search..." value={searchGlobal} onChange={e => setSearchGlobal(e.target.value)} allowClear />
      </div>

      <Table
        columns={columns}
        dataSource={invoices.filter(({ taxInvoiceDataInfo }) => 
          (!searchId || taxInvoiceDataInfo?.invoiceNo?.includes(searchId)) &&
          (!searchGlobal || Object.values(taxInvoiceDataInfo || {}).some(v =>
            String(v).toLowerCase().includes(searchGlobal.toLowerCase())
          ))
        )}
        rowKey={(record) => record?.taxInvoiceDataInfo?.invoiceNo || Math.random()}
        loading={loading}
        scroll={{ x: true }}
      />

      <PaymentUpdateModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={fetchInvoices}
        invoiceData={selectedInvoice}
      />
    </div>
  );
};

export default InvoiceDashboard;
